import React, { Component } from 'react';

import CafeteriaRecipeComponent from './CafeteriaRecipeComponent';
import { unitLabel } from '../utils/supply';

var config = process.env;

// Form de ítem de cafetería (gastronomía, productType=4). Modo alta y edición.
// Emite los datos por this.props.onChangeData(value).
//
// UX (ENI-90): en vez de exponer los flags técnicos (is_recipe_based, envasado…), se hace
// UNA pregunta guiada — "¿Cómo se maneja este ítem?" — con 3 respuestas en lenguaje llano:
//   · packaged  -> se vende cerrado (lata/botella): crea el insumo + receta 1:1 (atajo).
//   · recipe    -> se prepara con ingredientes: descuenta insumos por receta al vender.
//   · nostock   -> sin control de stock: siempre disponible, no descuenta nada.
// La terminología técnica (envasado / consumible por receta / sin control) se muestra SOLO
// a admin (rol 1), como nota al costado; el operador ve solo el lenguaje simple.
// Mismo orden que los bloques del POS, para que quien carga el menú elija la categoría en el
// orden en que después la va a encontrar el mozo en el mostrador.
const CATEGORIES = [
    { value: 'cold_drink', label: 'Bebida fría' },
    { value: 'hot_drink', label: 'Bebida caliente' },
    { value: 'bakery', label: 'Panadería' },
    { value: 'pizzeria', label: 'Pizzería' },
    { value: 'snack', label: 'Snack' },
    { value: 'other', label: 'Otro' },
];

class FormCafeteriaItemComponent extends Component {
    constructor(props) {
        super(props);
        const initial = props.initial || {};
        // Modo derivado del ítem existente (edición) o default (alta). En alta el default es
        // "envasado" (se vende cerrado), que es el flujo primario.
        let mode = 'packaged';
        if (props.editMode) {
            mode = (initial.isRecipeBased != null && Number(initial.isRecipeBased) === 0) ? 'nostock' : 'recipe';
        }
        this.state = {
            name: initial.name || '',
            price: initial.price != null ? initial.price : '',
            // ENI-111: precio especial de empleado (opcional). '' = sin precio de empleado.
            employeePrice: initial.employeePrice != null ? initial.employeePrice : '',
            // 'other' y no la primera de la lista: en alta nadie eligió todavía, y meter el
            // ítem en una categoría concreta por descarte lo esconde en el bloque equivocado
            // del POS. 'other' es el default honesto y salta a la vista que falta clasificar.
            category: initial.category || 'other',
            mode,
            requires_prep: initial.requiresPrep != null ? Number(initial.requiresPrep) === 1 : true,
            stock: '',             // solo alta + packaged + supplyMode 'new'
            // Envasado (alta): por defecto vincular un insumo existente ('existing'); si no hay
            // insumos consumibles cargados, fetchSupplies cae a crear uno nuevo ('new').
            supplyMode: 'existing',
            supplies: [],          // insumos consumibles existentes (para vincular)
            linkSupplyCode: '',    // insumo a vincular 1:1 cuando supplyMode === 'existing'
            recipeLines: [],       // solo alta + recipe
            description: initial.description || '',
            // ENI-231: costo. Con receta lo DERIVA el backend de los insumos y acá es de sólo
            // lectura; sin receta no hay de dónde derivarlo y se carga a mano.
            cost: initial.cost != null ? initial.cost : '',
        };
    }

    componentDidMount() {
        // Envasado (solo alta): se puede vincular un insumo consumible existente en vez de
        // crear uno nuevo. Precargamos la lista para el selector. En edición no aplica.
        if (!this.props.editMode) {
            this.fetchSupplies();
        }
    }

    fetchSupplies = () => {
        const base = config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
        fetch(base + 'cafeteria/supplies', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') },
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) {
                    return; // sin lista: "crear insumo nuevo" sigue disponible
                }
                // ENI-192: se ofrecen TODOS los insumos. El vínculo 1:1 del envasado ya no exige
                // is_consumable=1 — un envasado cargado como insumo simple es justamente el caso
                // más natural de este atajo.
                const supplies = (data.data || []);
                // Sin ningún insumo cargado no se puede vincular: caer a "crear insumo nuevo".
                this.setState((prev) => (
                    supplies.length === 0 ? { supplies, supplyMode: 'new' } : { supplies }
                ));
            })
            .catch(() => { /* silencioso: el modo "insumo nuevo" no depende de esta lista */ });
    }

    handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        this.setState({ [name]: type === 'checkbox' ? checked : value });
    }

    setMode = (mode) => {
        if (mode === this.state.mode) {
            return;
        }
        // En edición, pasar a "sin control" apaga el control de stock: confirmar.
        if (this.props.editMode && mode === 'nostock') {
            if (!window.confirm('«Sin control de stock» hace que el ítem esté siempre disponible y deje de descontar ingredientes. ¿Seguro?')) {
                return;
            }
        }
        const next = { mode };
        if (mode === 'packaged') {
            next.requires_prep = false; // un envasado se entrega directo
        } else if (mode === 'recipe') {
            next.requires_prep = true;
        }
        this.setState(next);
    }

    isValid = () => {
        const price = Number(this.state.price);
        return this.state.name.trim() !== '' && price > 0;
    }

    submit = () => {
        if (!this.isValid()) {
            alert('Completá nombre y precio (> 0).');
            return;
        }
        // ENI-111 (D-09): precio de empleado opcional; si viene, > 0 y <= precio normal.
        if (this.state.employeePrice !== '') {
            const emp = Number(this.state.employeePrice);
            if (!(emp > 0) || emp > Number(this.state.price)) {
                alert('El precio de empleado debe ser mayor a 0 y no mayor al precio normal.');
                return;
            }
        }
        const base = {
            name: this.state.name,
            price: this.state.price,
            // '' => null: en edición limpia un precio de empleado previo (backend usa 'campo' in data).
            employeePrice: this.state.employeePrice !== '' ? Number(this.state.employeePrice) : null,
            category: this.state.category,
            description: this.state.description,
        };
        // Envasado (solo alta): el backend crea o vincula el insumo + ítem + receta 1:1.
        if (!this.props.editMode && this.state.mode === 'packaged') {
            // Vincular un insumo consumible existente: mandamos su código; el backend NO crea insumo.
            if (this.state.supplyMode === 'existing') {
                if (this.state.linkSupplyCode === '') {
                    alert('Elegí el insumo existente a vincular (o creá uno nuevo).');
                    return;
                }
                this.props.onChangeData({ ...base, packaged: true, supplyCode: this.state.linkSupplyCode });
                return;
            }
            // Crear un insumo nuevo (comportamiento por defecto): mandamos el stock inicial.
            this.props.onChangeData({
                ...base,
                packaged: true,
                stock: this.state.stock !== '' ? Number(this.state.stock) : 0,
            });
            return;
        }
        const isRecipeBased = this.state.mode === 'recipe';
        const payload = { ...base, requires_prep: this.state.requires_prep, is_recipe_based: isRecipeBased };
        // ENI-231: el costo manual se manda SOLO en "sin control de stock". Para un ítem con
        // receta el costo lo derivan los insumos, y mandarlo acá escribiría un product.cost que
        // la lectura ignora — un número muerto en la base que después alguien lee como real.
        if (!isRecipeBased) {
            const cost = this.state.cost === '' ? 0 : Number(this.state.cost);
            if (!(cost >= 0)) {
                alert('El costo no puede ser negativo.');
                return;
            }
            payload.cost = cost;
        }
        // Alta con ingredientes: la receta va en el body (constitutiva).
        if (!this.props.editMode && isRecipeBased) {
            const lines = (this.state.recipeLines || []).map((l) => ({ supplyCode: l.supplyCode, quantity: Number(l.quantity) }));
            if (lines.some((l) => !(l.quantity > 0))) {
                alert('Cada ingrediente necesita una cantidad > 0 (o quitalo).');
                return;
            }
            payload.recipe = lines;
        }
        this.props.onChangeData(payload);
    }

    field(label, name, type = 'text', extra = {}) {
        return (
            <div className="form-group">
                <label>{label}</label>
                <input
                    type={type}
                    name={name}
                    className="form-control"
                    value={this.state[name]}
                    onChange={this.handleChange}
                    {...extra}
                />
            </div>
        );
    }

    // ENI-231: bloque de costo. Dos caras según de dónde sale el número:
    //  · sin receta -> input editable (es el único lugar donde se puede cargar).
    //  · con receta -> sólo lectura, con el costo derivado de los insumos y el margen contra el
    //    precio. Es el número que se va a persistir en la venta, así que mostrarlo acá es lo que
    //    permite ver el margen ANTES de vender, no recién en el ticket.
    costBlock() {
        const initial = this.props.initial || {};
        const isRecipeBased = this.state.mode === 'recipe';

        if (!isRecipeBased) {
            return this.field('Costo', 'cost', 'number', { min: 0, step: '0.01', placeholder: '0' });
        }

        // Alta con ingredientes: todavía no hay receta guardada de la cual derivar.
        if (!this.props.editMode) {
            return (
                <div className="form-group">
                    <label>Costo</label>
                    <input type="text" className="form-control" value="—" readOnly disabled />
                    <small className="text-muted">Se calcula de los ingredientes</small>
                </div>
            );
        }

        const cost = Number(initial.cost || 0);
        const price = Number(this.state.price || 0);
        const margin = price > 0 ? (100 * (price - cost) / price) : null;
        return (
            <div className="form-group">
                <label>Costo</label>
                <input type="text" className="form-control" value={`$ ${cost.toFixed(2)}`} readOnly disabled />
                {initial.costComplete === false
                    ? (<small className="text-danger">Parcial: falta cargar el costo de algún ingrediente</small>)
                    : (<small className="text-muted">
                        Calculado de los ingredientes{margin != null ? ` · margen ${margin.toFixed(0)}%` : ''}
                    </small>)}
            </div>
        );
    }

    // Nota técnica al costado, SOLO visible para admin (rol 1).
    adminNote(text) {
        if (Number(this.props.role) !== 1) {
            return null;
        }
        return (<small className="text-muted"> · {text}</small>);
    }

    render() {
        const editMode = !!this.props.editMode;
        const { mode } = this.state;
        // En edición no se ofrece "envasado" (el ítem ya existe con su insumo/receta).
        const options = [];
        if (!editMode) {
            options.push({ v: 'packaged', label: 'Se vende cerrado', sub: 'lata, botella, snack envasado', note: 'envasado (crea insumo + receta 1:1)' });
        }
        options.push({ v: 'recipe', label: 'Se prepara con ingredientes', sub: 'descuenta ingredientes al vender (café, leche…)', note: 'consumible por receta' });
        // "Sin control de stock" solo se ofrece en edición (para ítems que ya existen sin
        // control); en alta no, todo ítem nuevo lleva control de stock por insumo/receta.
        if (editMode) {
            options.push({ v: 'nostock', label: 'Sin control de stock', sub: 'siempre disponible, no descuenta nada', note: 'sin control de stock' });
        }

        return (
            <div className="card card-small mb-4">
                <div className="card-body p-4">
                    <h6 className="text-uppercase text-muted">Ítem de cafetería</h6>
                    <div className="row">
                        <div className="col-md-4">{this.field('Nombre *', 'name')}</div>
                        <div className="col-md-2">{this.field('Precio *', 'price', 'number')}</div>
                        <div className="col-md-2">{this.costBlock()}</div>
                        <div className="col-md-2">{this.field('Precio empleado', 'employeePrice', 'number', { min: 0, placeholder: 'opcional' })}</div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Categoría</label>
                                <select name="category" className="form-control" value={this.state.category} onChange={this.handleChange}>
                                    {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Pregunta guiada: reemplaza los flags técnicos. */}
                    <div className="form-group">
                        <label className="d-block font-weight-bold mb-2">¿Cómo se maneja este ítem?</label>
                        {options.map((o) => (
                            <label key={o.v} className="d-block mb-1" style={{ cursor: 'pointer' }}>
                                <input type="radio" name="mode" checked={mode === o.v} onChange={() => this.setMode(o.v)} />
                                {' '}<span className="font-weight-bold">{o.label}</span>
                                <span className="text-muted"> — {o.sub}</span>
                                {this.adminNote(o.note)}
                            </label>
                        ))}
                    </div>

                    {mode === 'packaged' && (
                        <div className="mb-2">
                            {/* El envasado se vincula 1:1 a un insumo consumible: uno nuevo o uno existente. */}
                            <label className="d-block font-weight-bold mb-2">Insumo del envasado</label>
                            <label className="d-block mb-1" style={{ cursor: 'pointer' }}>
                                <input type="radio" name="supplyMode" checked={this.state.supplyMode === 'new'} onChange={() => this.setState({ supplyMode: 'new' })} />
                                {' '}Crear un insumo nuevo
                                {this.adminNote('crea el insumo consumible + receta 1:1')}
                            </label>
                            <label className="d-block mb-2" style={{ cursor: this.state.supplies.length === 0 ? 'not-allowed' : 'pointer' }}>
                                <input type="radio" name="supplyMode" checked={this.state.supplyMode === 'existing'} disabled={this.state.supplies.length === 0} onChange={() => this.setState({ supplyMode: 'existing' })} />
                                {' '}Vincular un insumo existente
                                {this.state.supplies.length === 0 && <small className="text-muted"> · no hay insumos cargados</small>}
                                {this.adminNote('reusa un insumo existente + receta 1:1')}
                            </label>

                            {this.state.supplyMode === 'existing' ? (
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Insumo a vincular</label>
                                            <select name="linkSupplyCode" className="form-control" value={this.state.linkSupplyCode} onChange={this.handleChange}>
                                                <option value="">Elegí un insumo…</option>
                                                {this.state.supplies.map((s) => (<option key={s.code} value={s.code}>{s.name} ({unitLabel(s)})</option>))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <small className="text-muted">Se vincula 1:1 con este insumo existente; el stock del ítem sigue el de ese insumo (no se crea uno nuevo).</small>
                                    </div>
                                </div>
                            ) : (
                                <div className="row">
                                    <div className="col-md-4">{this.field('Stock inicial del insumo (unidades)', 'stock', 'number')}</div>
                                    <div className="col-12">
                                        {/* ENI-90: dejar explícito que el stock vive en el insumo, no en el ítem de menú. */}
                                        <small className="text-muted">Un envasado crea un insumo aparte; el stock vive en ese insumo, no en el ítem de menú.</small>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {mode !== 'packaged' && (
                        <div className="form-group">
                            <label>
                                <input type="checkbox" name="requires_prep" checked={this.state.requires_prep} onChange={this.handleChange} />
                                {' '}Sale en la comanda para preparar ("A PREPARAR")
                            </label>
                        </div>
                    )}

                    {mode === 'recipe' && !editMode && (
                        <CafeteriaRecipeComponent
                            value={this.state.recipeLines}
                            onChange={(lines) => this.setState({ recipeLines: lines })}
                        />
                    )}

                    {mode === 'nostock' && (
                        <p className="text-muted">Este ítem estará siempre disponible en el POS y no descuenta ingredientes.</p>
                    )}

                    <div className="form-group mt-2">
                        <label>Descripción</label>
                        <textarea name="description" className="form-control" rows="2" value={this.state.description} onChange={this.handleChange} />
                    </div>

                    <button type="button" className="btn btn-accent" disabled={this.props.disabled} onClick={this.submit}>
                        {editMode ? 'Guardar' : (mode === 'packaged' ? 'Crear envasado' : 'Crear ítem')}
                    </button>
                </div>
            </div>
        );
    }
}

export default FormCafeteriaItemComponent;
